/**
 * Funnels client-side errors (React crashes, uncaught JS, promise
 * rejections, unexpected 5xx API responses) to the backend admin alert
 * pipeline. Includes the last ~30 breadcrumbs so the alert tells the
 * reviewer what the user was doing before the crash.
 *
 * Filter policy: we DO NOT report expected user-facing errors like
 *   401 (wrong password / session expired)
 *   403 (insufficient permissions)
 *   404 (record not found)
 *   400 (validation)
 * These are normal UX paths, not bugs. Only 5xx and network failures
 * and truly uncaught exceptions make it through.
 *
 * Dedup: a local set of signatures (message + top-of-stack) suppresses
 * repeated reports within a session so a broken component re-rendering
 * 50 times/second doesn't flood the admin inbox. Backend adds its own
 * 1h dedup window on top of this.
 */

import { getBreadcrumbs } from "./breadcrumbs";

const API_BASE = `${import.meta.env.VITE_BACKEND_URL}/api`;
const REPORT_PATH = `${API_BASE}/client-errors`;

const sessionSignatures = new Set();

function signature(message, stack) {
  const first = (stack || "").split("\n").slice(0, 2).join(" ");
  return `${message || ""}|${first}`.slice(0, 400);
}

function currentUser() {
  try {
    const u = localStorage.getItem("altiv_user_cache");
    return u ? JSON.parse(u) : null;
  } catch {
    return null;
  }
}

async function send(payload) {
  try {
    // fetch + keepalive sigue saliendo mid-unload y sí soporta CORS preflight,
    // a diferencia de sendBeacon con Content-Type application/json (que el
    // browser descarta en cross-origin sin preflight posible).
    await fetch(REPORT_PATH, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Never throw from an error reporter.
  }
}

export function reportClientError({ kind, severity = "error", message, stack, extra }) {
  try {
    const sig = signature(message, stack);
    if (sessionSignatures.has(sig)) return;
    sessionSignatures.add(sig);

    const user = currentUser();
    const payload = {
      kind: kind || "frontend_error",
      severity,
      message: (message || "").slice(0, 1000),
      stack: (stack || "").slice(0, 5000),
      url: typeof window !== "undefined" ? window.location.href : "",
      user_agent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      user_email: user?.email || null,
      user_role: user?.role || null,
      breadcrumbs: getBreadcrumbs(),
      ...(extra ? { extra } : {}),
    };
    send(payload);
  } catch {
    // Never throw from an error reporter.
  }
}

// When a script loaded from a *different* origin throws, the browser
// sanitizes the `error` event down to a bare "Script error." with no
// `event.error` (so no stack), an empty filename and lineno/colno 0. This is a
// hard CORS privacy rule — there is, by design, NOTHING actionable in it. The
// throwing script is almost always third-party: a browser extension / content
// blocker / password-manager content script, or an embedded widget. Our own
// bundle is emitted with `crossorigin` (Vite default) and served same-origin,
// so genuine Altiv crashes still arrive with a real message + stack and are
// NOT caught by this guard. Sentry already drops "Script error." via its
// default inbound filters; mirror that here so these blind cross-origin events
// don't surface as un-debuggable `frontend_uncaught` admin alerts.
function isOpaqueCrossOriginError(event) {
  const hasStack = Boolean(event && event.error && event.error.stack);
  const message = ((event && event.message) || "").trim();
  return !hasStack && /^script error\.?$/i.test(message);
}

// ── Install global handlers ────────────────────────────────────────────────
// Safe to call more than once; subsequent calls replace the previous handler.
let _installed = false;
export function installGlobalErrorReporter() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;

  window.addEventListener("error", (event) => {
    if (isOpaqueCrossOriginError(event)) return;
    reportClientError({
      kind: "frontend_uncaught",
      severity: "error",
      message: event.message || (event.error && event.error.message) || "Unknown error",
      stack: (event.error && event.error.stack) || "",
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? `Unhandled promise: ${reason.message}`
        : `Unhandled promise: ${String(reason).slice(0, 200)}`;
    const stack = reason instanceof Error ? reason.stack : "";
    reportClientError({
      kind: "frontend_promise_rejection",
      severity: "error",
      message,
      stack,
    });
  });
}
